import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Music2, Search, FileText, Handshake, ArrowRight, Sparkles } from "lucide-react";
import TestimonialMarquee from "@/components/shared/TestimonialMarquee";

const EXAMPLES = [
  "Find a guitarist for Friday",
  "Book a DJ for an event",
  "Hire a live band",
  "Find a venue",
  "Post a gig",
];

export default function Landing() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.loginRequired) {
      toast.info("Please sign in to view full profiles.");
    }
  }, [location.state]);

  const startGoogle = () => {
    const redirectUri = window.location.origin + "/auth/callback";
    const params = new URLSearchParams({
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: "code",
      scope: "openid email profile",
      access_type: "offline",
      prompt: "consent",
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };

  return (
    <div className="min-h-screen bg-[#FCFCFD]">
      <header className="sticky top-0 z-40 w-full border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#0F172A] text-white grid place-items-center">
              <Music2 className="w-4 h-4" />
            </div>
            <span className="font-display font-bold text-lg tracking-tight">Showfex</span>
          </div>
          <nav className="ml-8 hidden md:flex gap-6 text-sm text-slate-600">
            <button onClick={() => navigate("/explore")} className="hover:text-slate-900" data-testid="pub-explore">Explore</button>
            <button onClick={() => navigate("/how-it-works")} className="hover:text-slate-900" data-testid="pub-how">How it works</button>
          </nav>
          <div className="ml-auto flex gap-2">
            <Button variant="ghost" onClick={startGoogle} data-testid="header-signin">Sign in</Button>
            <Button onClick={startGoogle} className="bg-[#0F172A] hover:bg-[#1E293B] text-white" data-testid="header-signup">Get started</Button>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-28 relative">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#4F46E5]">
              <Sparkles className="w-3.5 h-3.5" /> A booking marketplace for music
            </span>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
              Find or create real music opportunities.
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl">
              Book musicians, find gigs, hire music professionals and turn requirements into confirmed bookings — all in one place.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" onClick={() => navigate("/explore")} className="bg-[#4F46E5] hover:bg-[#4338CA] text-white" data-testid="cta-find-talent">
                Find Music / Talent <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button size="lg" variant="outline" onClick={startGoogle} data-testid="cta-offer-services">
                Offer Music Services
              </Button>
            </div>
            <div className="mt-10 flex flex-wrap gap-2">
              {EXAMPLES.map((e) => (
                <span key={e} className="text-sm bg-white border border-slate-200 rounded-full px-3 py-1.5 text-slate-700">{e}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="how" className="max-w-7xl mx-auto px-6 py-16">
        <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">How it works</h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { icon: Music2, t: "Create your profile", d: "Musician, band, DJ or venue — pick your identity." },
            { icon: Search, t: "Explore", d: "Browse gigs and talent with real city, genre and rate filters." },
            { icon: FileText, t: "Apply / receive", d: "Send a structured application or manage requests." },
            { icon: Handshake, t: "Connect directly", d: "Once accepted, contact is unlocked — continue on WhatsApp." },
          ].map((s, i) => (
            <Card key={i} className="p-5 border-slate-200">
              <s.icon className="w-5 h-5 text-[#4F46E5]" />
              <div className="mt-3 font-semibold text-slate-900">{s.t}</div>
              <p className="mt-1 text-sm text-slate-600">{s.d}</p>
            </Card>
          ))}
        </div>
      </section>

      <TestimonialMarquee />

      <footer className="py-10 text-center text-sm text-slate-500">© Showfex Marketplace</footer>
    </div>
  );
}
